/**
 * Admin portfolio selectors (PX-W7.9, TASK-17A2C062) — derived KPI figures
 * and row view models over the one-request read, as memoised `createSelector`
 * outputs (standing rule 1: a derived value never lives in a reducer or a
 * component body).
 */

import { createSelector } from '@reduxjs/toolkit'

import type { StatusTone } from '@/components/px/StatusPill'
import { selectAdminPortfolioState } from './slices/adminPortfolioSlice'
import type { AdminCurrentGate, AdminPortfolioRow } from './types'

/** A stable empty-array reference — never a fresh `[]` literal per selector call. */
const NO_ROWS: readonly AdminPortfolioRow[] = []

/** The portfolio's project rows, in the server's own order. */
export const selectAdminPortfolioRows = createSelector(
  [selectAdminPortfolioState],
  (state): readonly AdminPortfolioRow[] => (state.view === null ? NO_ROWS : state.view.projects)
)

export interface AdminKpis {
  engagements: number
  /** Distinct people linked across every row — `userId`, else `id:`-prefixed legacy `id`. */
  peopleLinked: number
  /** The organisation's total user count, or `null` while the view has not loaded. */
  orgUserCount: number | null
  /** Total member rows across every engagement (a person on two engagements counts twice). */
  memberRows: number
  /** Engagements whose current gate is open (neither approved nor absent). */
  gatesOpen: number
  gatesClientSignOff: number
  gatesInternal: number
}

/** The distinct-person key for a roster row, or `null` when it carries neither id. */
const memberKey = (member: { userId?: string; id?: string }): string | null =>
  member.userId ?? (member.id !== undefined && member.id !== '' ? `id:${member.id}` : null)

export const selectAdminKpis = createSelector(
  [selectAdminPortfolioRows, selectAdminPortfolioState],
  (rows, state): AdminKpis => {
    const distinctPeople = new Set<string>()
    let memberRows = 0
    let gatesOpen = 0
    let gatesClientSignOff = 0
    let gatesInternal = 0
    for (const row of rows) {
      for (const member of row.members) {
        memberRows += 1
        const key = memberKey(member)
        if (key !== null) distinctPeople.add(key)
      }
      if (row.currentGate?.open === true) {
        gatesOpen += 1
        if (row.currentGate.type === 'CLIENT_SIGN_OFF') gatesClientSignOff += 1
        else gatesInternal += 1
      }
    }
    return {
      engagements: rows.length,
      peopleLinked: distinctPeople.size,
      orgUserCount: state.view?.orgUserCount ?? null,
      memberRows,
      gatesOpen,
      gatesClientSignOff,
      gatesInternal
    }
  }
)

/** The status pill tone for a project's current gate (AC-11). */
export const gateTone = (gate: AdminCurrentGate): StatusTone => {
  if (gate.status === 'RETURNED') return 'blocked'
  if (gate.status === 'APPROVED') return 'decided'
  if (gate.type === 'CLIENT_SIGN_OFF') return 'review'
  return 'neutral'
}
